import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, X, Sparkles, Gift, Map, Clock, Check, ShoppingBag, Target } from 'lucide-react';
import { usePet } from '../contexts/PetContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${sec.toString().padStart(2, '0')}s`;
  return `${sec}s`;
};

/* Simple SVG critter — uses pet image if available, else a kawaii bird */
function PetSprite({ petImage, walking }) {
  return (
    <div className="relative">
      <div
        className={`w-28 h-28 rounded-full border-[3px] border-black bg-white flex items-center justify-center shadow-[3px_3px_0_#000] overflow-hidden ${walking ? 'animate-bounce' : ''}`}
        style={{ animationDuration: walking ? '0.9s' : undefined }}
      >
        {petImage ? (
          <img src={petImage} alt="pet" className="w-full h-full object-cover" />
        ) : (
          <span className="text-5xl">🐤</span>
        )}
      </div>
      {/* Shadow */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-20 h-2 bg-black/20 rounded-full blur-sm" />
    </div>
  );
}

/* Decorative scene elements */
function SceneDecor({ sceneId }) {
  if (sceneId === 'natt') {
    return (
      <>
        <span className="absolute top-3 left-6 text-xl">⭐</span>
        <span className="absolute top-6 right-12 text-2xl">🌙</span>
        <span className="absolute top-12 left-1/3 text-lg">✨</span>
        <span className="absolute top-4 right-1/4 text-sm">⭐</span>
        <span className="absolute bottom-2 left-3 text-3xl">🌲</span>
        <span className="absolute bottom-2 right-3 text-3xl">🌲</span>
      </>
    );
  }
  if (sceneId === 'strand') {
    return (
      <>
        <span className="absolute top-3 right-5 text-3xl">☀️</span>
        <span className="absolute bottom-2 left-2 text-2xl">🐚</span>
        <span className="absolute bottom-3 right-6 text-2xl">🌴</span>
      </>
    );
  }
  if (sceneId === 'berg') {
    return (
      <>
        <span className="absolute top-2 left-4 text-3xl">⛰️</span>
        <span className="absolute top-4 right-6 text-2xl">☁️</span>
        <span className="absolute bottom-2 right-3 text-2xl">🌲</span>
      </>
    );
  }
  if (sceneId === 'ang') {
    return (
      <>
        <span className="absolute top-3 right-5 text-2xl">🦋</span>
        <span className="absolute bottom-2 left-3 text-xl">🌼</span>
        <span className="absolute bottom-2 right-12 text-xl">🌷</span>
        <span className="absolute bottom-3 left-1/3 text-lg">🌸</span>
      </>
    );
  }
  if (sceneId === 'regn') {
    return (
      <>
        <span className="absolute top-2 right-8 text-2xl">☁️</span>
        <span className="absolute top-4 left-6 text-xl">💧</span>
        <span className="absolute top-10 right-1/4 text-sm">💧</span>
        <span className="absolute bottom-3 left-2 text-3xl">🌳</span>
      </>
    );
  }
  // skog default
  return (
    <>
      <span className="absolute top-2 left-3 text-3xl">🌳</span>
      <span className="absolute top-4 right-5 text-3xl">🌲</span>
      <span className="absolute bottom-2 right-1/3 text-xl">🍄</span>
      <span className="absolute bottom-3 left-1/4 text-lg">🌿</span>
    </>
  );
}

/* ============================================================
   Choose Adventure Modal
   ============================================================ */
function ChooseAdventureModal({ open, onClose, adventures, energy, onStarted }) {
  const [busy, setBusy] = useState(null);
  if (!open) return null;
  const start = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/adventure/start`, { duration: id }, { withCredentials: true });
      toast.success('🐾 Äventyret börjar!');
      onStarted?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte starta');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="choose-adventure-modal">
      <div className="neo-card bg-white p-4 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2"><Map className="w-4 h-4" /> Välj äventyr</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="adventure-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs opacity-70 mb-3">Du har <strong>{energy} ⚡</strong> energi just nu.</p>
        <div className="space-y-2">
          {adventures.map((a) => {
            const enough = energy >= a.energy_cost;
            return (
              <button
                key={a.id}
                onClick={() => enough && start(a.id)}
                disabled={!enough || busy === a.id}
                className={`neo-card p-3 w-full text-left bg-white flex items-start gap-3 transition ${enough ? 'hover:bg-[#FDF0D5]' : 'opacity-50'}`}
                data-testid={`adventure-pick-${a.id}`}
              >
                <span className="text-3xl">{a.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <h4 className="font-bold text-sm">{a.label}</h4>
                    <span className="text-[11px] font-bold border-2 border-black rounded-full px-2 py-0.5 bg-[#FFE8A3]">
                      {a.energy_cost} ⚡
                    </span>
                  </div>
                  <p className="text-[11px] opacity-70 leading-snug">{a.subtitle}</p>
                  <div className="flex items-center gap-2 text-[11px] mt-1">
                    <span>+{a.reward_points} poäng</span>
                    <span>+{a.reward_peaches} 🍑</span>
                    {a.drop_chance > 0 && <span className="opacity-60">· chans till skatt</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Quick Tasks Drawer
   ============================================================ */
function QuickTasksDrawer({ open, onClose, tasks, onTaskComplete }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="quick-tasks-drawer">
      <div className="neo-card bg-white p-4 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold flex items-center gap-2"><Zap className="w-4 h-4 fill-[#FFD166] text-[#FFD166]" /> Mikro-tasks → energi</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="quick-tasks-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] opacity-70 mb-3 italic">En gång per dag per task. Små vinster bygger pet:s äventyr 💛</p>
        <div className="grid gap-1.5">
          {tasks.map((t) => (
            <button
              key={t.id}
              onClick={() => !t.done_today && onTaskComplete(t)}
              disabled={t.done_today}
              className={`neo-card p-2.5 flex items-center gap-2 transition ${t.done_today ? 'opacity-50 bg-[#E8FFE8]' : 'bg-white hover:bg-[#FFF5E6]'}`}
              data-testid={`quick-task-${t.id}`}
            >
              <span className="text-2xl">{t.emoji}</span>
              <span className={`flex-1 text-left text-sm font-bold ${t.done_today ? 'line-through' : ''}`}>{t.label}</span>
              {t.done_today ? (
                <Check className="w-5 h-5" />
              ) : (
                <span className="text-[11px] font-bold border-2 border-black rounded-full px-2 py-0.5 bg-[#FFE8A3]">+{t.energy} ⚡</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Claim Modal
   ============================================================ */
function ClaimModal({ open, onClose, result }) {
  if (!open || !result) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="adventure-claim-modal">
      <div className="neo-card bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF] p-5 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
        <div className="text-6xl mb-2 animate-bounce">{result.memory?.emoji || '🌟'}</div>
        <h3 className="font-black text-lg">Tillbaka från {result.memory?.label?.toLowerCase() || 'äventyret'}!</h3>
        <p className="text-xs opacity-70 mb-3">{result.memory?.scene?.label || ''}</p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="border-2 border-black rounded-xl p-2 bg-white">
            <div className="text-[10px] font-bold opacity-60 uppercase">Poäng</div>
            <div className="text-xl font-black">+{result.points_awarded || 0}</div>
          </div>
          <div className="border-2 border-black rounded-xl p-2 bg-[#FFE8F5]">
            <div className="text-[10px] font-bold opacity-60 uppercase">Peaches</div>
            <div className="text-xl font-black">+{result.memory?.peaches || 0} 🍑</div>
          </div>
        </div>
        {result.drop && (
          <div className="border-[3px] border-black rounded-xl p-3 bg-[#FFE8A3] mb-3" data-testid="adventure-drop">
            <div className="text-3xl mb-1">{result.drop.emoji}</div>
            <div className="font-bold text-sm">Hittade en sak!</div>
            <div className="text-xs opacity-80">{result.drop.label}</div>
          </div>
        )}
        {result.moon_bonus?.applied && (
          <div className="border-[3px] border-black rounded-xl p-3 bg-gradient-to-r from-[#1E1B4B] to-[#3D2A5F] text-white mb-3" data-testid="adventure-moon-bonus">
            <div className="flex items-center gap-2">
              <span className="text-3xl">{result.moon_bonus.emoji}</span>
              <div className="flex-1 text-left">
                <p className="text-[10px] font-bold tracking-wider opacity-70 uppercase">{result.moon_bonus.label}</p>
                <p className="text-xs">{result.moon_bonus.msg}</p>
              </div>
              <p className="font-bold">+{result.moon_bonus.peaches} 🍑</p>
            </div>
          </div>
        )}
        <button onClick={onClose} className="neo-btn w-full py-2.5 bg-[#B9FBC0] font-bold" data-testid="adventure-claim-close">
          Tack 💛
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   Memories Strip
   ============================================================ */
function MemoriesStrip({ memories }) {
  if (!memories || memories.length === 0) return null;
  return (
    <div className="neo-card p-3 bg-white" data-testid="memories-strip">
      <h4 className="font-bold text-xs mb-2 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5" /> Minnen från äventyren
      </h4>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {[...memories].reverse().slice(0, 12).map((m) => (
          <div
            key={m.id}
            className="shrink-0 w-20 border-2 border-black rounded-lg p-1.5 text-center"
            style={{ background: m.scene?.bg || '#FDF0D5', backgroundSize: 'cover' }}
            title={`${m.label} · ${m.scene?.label || ''} · +${m.points}p`}
            data-testid={`memory-card-${m.id}`}
          >
            <div className="text-2xl">{m.emoji}</div>
            <div className="text-[9px] font-bold truncate text-white drop-shadow-[1px_1px_0_rgba(0,0,0,0.7)]">
              {m.scene?.label || m.label}
            </div>
            <div className="text-[9px] font-bold text-white drop-shadow-[1px_1px_0_rgba(0,0,0,0.7)]">
              +{m.points} · {m.peaches}🍑
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   🍑 Peach Shop Modal
   ============================================================ */
const SHOP_TYPE_LABELS = {
  accessory: 'Accessoarer',
  scene: 'Scener',
  boost: 'Boost',
  cosmetic: 'Kosmetik',
};
const SHOP_TYPE_ORDER = ['accessory', 'scene', 'boost', 'cosmetic'];

function PeachShopModal({ open, onClose, onPurchase }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const { data: d } = await axios.get(`${API}/adventure/shop`, { withCredentials: true });
      setData(d);
    } catch { /* ignore */ }
  }, [open]);

  useEffect(() => { load(); }, [load]);

  if (!open) return null;

  const buy = async (item) => {
    setBusy(item.id);
    try {
      const { data: resp } = await axios.post(`${API}/adventure/shop/buy/${item.id}`, {}, { withCredentials: true });
      if (resp.energy_added > 0) {
        toast.success(`${item.emoji} +${resp.energy_added} ⚡!`);
      } else {
        toast.success(`${item.emoji} "${item.label}" upplåst!`);
      }
      load();
      onPurchase?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte köpa');
    } finally {
      setBusy(null);
    }
  };

  const grouped = {};
  (data?.items || []).forEach(it => {
    grouped[it.type] = grouped[it.type] || [];
    grouped[it.type].push(it);
  });

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="peach-shop-modal">
      <div className="neo-card bg-gradient-to-br from-[#FFE8F5] via-white to-[#FFF5E6] p-4 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-black flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> 🍑 Peach-shop</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="peach-shop-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-xs mb-3 flex items-center gap-2">
          <span className="bg-white border-2 border-black rounded-full px-2.5 py-1 font-bold">🍑 {data?.peaches ?? 0}</span>
          <span className="opacity-70 italic">Spendera dina äventyrs-belöningar</span>
        </div>
        {SHOP_TYPE_ORDER.map(typeKey => {
          const items = grouped[typeKey] || [];
          if (!items.length) return null;
          return (
            <div key={typeKey} className="mb-3" data-testid={`peach-shop-section-${typeKey}`}>
              <h4 className="text-[11px] font-bold uppercase opacity-60 mb-1.5 tracking-wider">
                {SHOP_TYPE_LABELS[typeKey] || typeKey}
              </h4>
              <div className="grid gap-1.5">
                {items.map(it => {
                  const canAfford = (data?.peaches ?? 0) >= it.cost;
                  const blocked = (it.owned && it.type !== 'boost');
                  return (
                    <div
                      key={it.id}
                      className={`neo-card p-2.5 flex items-center gap-2.5 ${blocked ? 'bg-[#E8FFE8] opacity-70' : 'bg-white'}`}
                      data-testid={`peach-shop-item-${it.id}`}
                    >
                      <span className="text-3xl shrink-0">{it.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm leading-tight">{it.label}</div>
                        <div className="text-[11px] opacity-70 leading-snug">{it.desc}</div>
                      </div>
                      <button
                        onClick={() => !blocked && canAfford && buy(it)}
                        disabled={blocked || !canAfford || busy === it.id}
                        className={`neo-btn px-2.5 py-1.5 text-xs font-bold flex items-center gap-1 shrink-0 ${
                          blocked ? 'bg-[#B9FBC0]' : canAfford ? 'bg-[#FFE8A3]' : 'bg-white opacity-50'
                        }`}
                        data-testid={`peach-shop-buy-${it.id}`}
                      >
                        {blocked ? <><Check className="w-3 h-3" /> Ägd</> : `${it.cost} 🍑`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        <p className="text-[10px] opacity-60 text-center italic mt-2">
          Tjäna 🍑 genom att skicka pet på äventyr 💛
        </p>
      </div>
    </div>
  );
}

/* ============================================================
   Main AdventureScene
   ============================================================ */
export default function AdventureScene({ onPointsChange, onQuickFocus }) {
  const { pet } = usePet();
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [chooseOpen, setChooseOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const fetchState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/adventure/state`, { withCredentials: true });
      setState(data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  // Tick clock every second when adventure ongoing
  useEffect(() => {
    if (!state?.current_adventure) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [state?.current_adventure?.id]);

  const cur = state?.current_adventure;
  const secondsLeft = cur && cur.ends_at
    ? Math.max(0, Math.floor((new Date(cur.ends_at).getTime() - now) / 1000))
    : 0;
  const ready = !!cur && secondsLeft === 0;

  const completeQuickTask = async (task) => {
    try {
      const { data } = await axios.post(`${API}/adventure/quick-task/${task.id}`, {}, { withCredentials: true });
      if (data.already_done_today) {
        toast('Redan klart idag 💛');
      } else {
        toast.success(`+${data.energy_gained} ⚡`);
        if (onPointsChange) onPointsChange();
      }
      fetchState();
    } catch (e) {
      toast.error('Kunde inte spara');
    }
  };

  const claim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const { data } = await axios.post(`${API}/adventure/claim`, {}, { withCredentials: true });
      setClaimResult(data);
      fetchState();
      if (onPointsChange) onPointsChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte hämta hem');
    } finally {
      setClaiming(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Avbryt äventyret? Du får 50% av energin tillbaka.')) return;
    try {
      await axios.post(`${API}/adventure/cancel`, {}, { withCredentials: true });
      toast('Avbrutet — 50% energi tillbaka');
      fetchState();
    } catch (e) { /* ignore */ }
  };

  if (!state) {
    return (
      <div className="neo-card p-6 mb-4 bg-white text-center text-sm opacity-60" data-testid="adventure-loading">
        Laddar äventyret...
      </div>
    );
  }

  const scene = cur?.scene || state.scene;
  const energyPct = Math.round((state.energy_current / state.energy_max) * 100);
  const adventurePct = cur ? Math.round((1 - secondsLeft / ((new Date(cur.ends_at).getTime() - new Date(cur.started_at).getTime()) / 1000)) * 100) : 0;

  return (
    <>
      <div className="space-y-2 mb-4" data-testid="adventure-scene">
        {/* Scene panel */}
        <div
          className="relative rounded-3xl border-[3px] border-black overflow-hidden shadow-[5px_5px_0_#000]"
          style={{ background: scene?.bg || 'linear-gradient(180deg,#BFE5C9,#6FBF82)', minHeight: 220 }}
        >
          <SceneDecor sceneId={scene?.id} />

          {/* Top bar: peaches + energy */}
          <div className="absolute top-0 inset-x-0 p-2 flex items-center justify-between gap-2 z-10">
            <button
              onClick={() => setShopOpen(true)}
              className="bg-white border-2 border-black rounded-full px-2.5 py-1 text-xs font-bold flex items-center gap-1 shadow-[2px_2px_0_#000] hover:bg-[#FFF5E6] transition"
              data-testid="peaches-counter"
              title="Öppna Peach-shop"
            >
              🍑 {state.peaches}
            </button>
            <div className="flex-1 mx-2 max-w-xs">
              <div className="h-3 border-2 border-black rounded-full bg-white overflow-hidden shadow-[2px_2px_0_#000]" title={`Energi ${state.energy_current}/${state.energy_max}`} data-testid="energy-bar">
                <div className="h-full transition-all duration-500" style={{
                  width: `${energyPct}%`,
                  background: 'linear-gradient(90deg, #FFE066, #FFB347)',
                }} />
              </div>
              <div className="text-[10px] text-center font-bold text-white drop-shadow-[1px_1px_0_rgba(0,0,0,0.6)] mt-0.5">
                {state.energy_current} / {state.energy_max} ⚡
              </div>
            </div>
            <button
              onClick={() => setTasksOpen(true)}
              className="neo-btn px-2 py-1 bg-[#FFE8A3] text-xs font-bold flex items-center gap-1 shrink-0"
              data-testid="adventure-tasks-btn"
              title="Mikro-tasks för energi"
            >
              <Zap className="w-3.5 h-3.5 fill-current" /> +
            </button>
          </div>

          {/* Pet centered */}
          <div className="flex items-end justify-center pt-12 pb-12 relative z-[1]">
            <PetSprite
              petImage={pet?.image_url}
              walking={!!cur && !ready}
            />
          </div>

          {/* Scene label */}
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-sm border border-black rounded-full px-2 py-0.5 text-[10px] font-bold">
            {scene?.emoji} {scene?.label}
          </div>
        </div>

        {/* 🌙 Moon-fas-bonus idag (tied to Vit Magi rytm) */}
        {state.moon_today && state.moon_today.bonus_peaches > 0 && (
          <div
            className="neo-card px-3 py-2 mt-2 bg-gradient-to-r from-[#1E1B4B] via-[#3D2A5F] to-[#1E1B4B] text-white flex items-center gap-2"
            data-testid="moon-bonus-banner"
            title={state.moon_today.msg}
          >
            <span className="text-2xl">{state.moon_today.emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold tracking-wider uppercase opacity-70">{state.moon_today.label}</p>
              <p className="text-xs truncate">{state.moon_today.msg}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[9px] opacity-70 font-bold">EXTRA</p>
              <p className="font-bold">+{state.moon_today.bonus_peaches} 🍑</p>
            </div>
          </div>
        )}

        {/* Adventure status / actions */}
        {cur ? (
          <div className="neo-card p-3" style={{ background: ready ? '#B9FBC0' : '#FDF0D5' }} data-testid="adventure-status">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4" />
              <div className="flex-1">
                <div className="text-xs opacity-70 font-bold uppercase">{ready ? 'Hemma!' : 'På äventyr'}</div>
                <div className="font-black text-sm">{cur.emoji} {cur.label}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] opacity-60 font-bold uppercase">{ready ? 'Klar' : 'Hem om'}</div>
                <div className="font-black text-sm font-mono" data-testid="adventure-timer">
                  {ready ? '✨ nu!' : fmtTime(secondsLeft)}
                </div>
              </div>
            </div>
            <div className="h-2 border-2 border-black rounded-full overflow-hidden bg-white">
              <div className="h-full transition-all duration-500" style={{
                width: `${adventurePct}%`,
                background: ready ? 'linear-gradient(90deg,#B9FBC0,#C8B6FF,#FFC8DD)' : '#A2D2FF',
              }} />
            </div>
            <div className="flex gap-2 mt-2">
              {ready ? (
                <button
                  onClick={claim}
                  disabled={claiming}
                  className="neo-btn flex-1 py-2 bg-white font-bold flex items-center justify-center gap-1 text-sm"
                  data-testid="adventure-claim-btn"
                >
                  <Gift className="w-4 h-4" /> Hämta hem belöning
                </button>
              ) : (
                <button onClick={cancel} className="neo-btn px-3 py-1.5 bg-white text-xs flex items-center gap-1" data-testid="adventure-cancel-btn">
                  Avbryt
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setChooseOpen(true)}
              className="neo-btn flex-1 py-2.5 bg-gradient-to-br from-[#FFC8DD] to-[#A2D2FF] font-bold flex items-center justify-center gap-2 text-sm"
              data-testid="adventure-start-btn"
            >
              <Map className="w-4 h-4" /> Skicka iväg på äventyr
            </button>
            <button
              onClick={() => setTasksOpen(true)}
              className="neo-btn px-3 py-2.5 bg-[#FFE8A3] font-bold flex items-center gap-1 text-sm"
              data-testid="adventure-tasks-btn-alt"
              title="Mikro-tasks"
            >
              <Zap className="w-4 h-4 fill-current" /> Energi
            </button>
          </div>
        )}

        <MemoriesStrip memories={state.memories} />
      </div>

      <ChooseAdventureModal
        open={chooseOpen}
        onClose={() => setChooseOpen(false)}
        adventures={state.adventures || []}
        energy={state.energy_current}
        onStarted={fetchState}
      />
      <QuickTasksDrawer
        open={tasksOpen}
        onClose={() => setTasksOpen(false)}
        tasks={state.quick_tasks || []}
        onTaskComplete={completeQuickTask}
      />
      <ClaimModal
        open={!!claimResult}
        onClose={() => setClaimResult(null)}
        result={claimResult}
      />
      <PeachShopModal
        open={shopOpen}
        onClose={() => setShopOpen(false)}
        onPurchase={fetchState}
      />
    </>
  );
}
